import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { QRCodeSVG } from 'qrcode.react';
import './App.css';
import { logEvent } from './utils/logEvent';

const Setup2FA = ({ setAuthUsername, setIsAuthenticated }) => {
    const [provisioningUri, setProvisioningUri] = useState('');
    const [otp, setOtp] = useState('');
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const [loadingQr, setLoadingQr] = useState(true);
    const navigate = useNavigate();

    useEffect(() => {
        const generateQrCode = async () => {
            try {
                const tempToken = sessionStorage.getItem('temp_token');
                if (!tempToken) {
                    navigate('/login');
                    return;
                }
                const response = await fetch('/api/2fa/generate', {
                    method: 'POST',
                    headers: { 'Authorization': `Bearer ${tempToken}` }
                });
                if (response.ok) {
                    const data = await response.json();
                    setProvisioningUri(data.provisioning_uri);
                    logEvent('2fa_generate_qr_success', {}).catch(() => {});
                } else {
                    const data = await response.json().catch(() => ({}));
                    setError(data.error || 'Failed to generate QR code. Please login again.');
                    logEvent('2fa_generate_qr_failure', { error: data.error }).catch(() => {});
                }
            } catch (e) {
                setError('Network error. Please try again.');
                logEvent('2fa_generate_qr_failure', { error: e?.message || 'network_error' }).catch(() => {});
            } finally {
                setLoadingQr(false);
            }
        };
        generateQrCode();
    }, [navigate]);

    const handleVerify = async (e) => {
        e.preventDefault();
        setError('');
        setLoading(true);
        try {
            const tempToken = sessionStorage.getItem('temp_token');
            const username = sessionStorage.getItem('username_2fa');
            if (!tempToken) {
                setError('Your session expired. Please log in again.');
                navigate('/login');
                return;
            }
            const response = await fetch('/api/2fa/verify', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${tempToken}`
                },
                body: JSON.stringify({ otp })
            });
            if (response.ok) {
                logEvent('login_success', { method: '2fa_setup', username }).catch(() => {});
                setAuthUsername(username || '');
                setIsAuthenticated(true);
                try { sessionStorage.setItem('tab_session', '1'); } catch (e) { /* ignore */ }
                sessionStorage.removeItem('temp_token');
                sessionStorage.removeItem('username_2fa');
                navigate('/');
            } else {
                const data = await response.json().catch(() => ({}));
                setError(data.error || 'Invalid code. Please try again.');
                logEvent('login_2fa_failure', { stage: 'setup', error: data.error }).catch(() => {});
            }
        } catch (e) {
            setError('Network error. Please try again.');
            logEvent('login_2fa_failure', { stage: 'setup', error: e?.message || 'network_error' }).catch(() => {});
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="login-container">
            <form onSubmit={handleVerify} className="login-form">
                <h2>Set Up Two-Factor Authentication</h2>
                <p>Scan the QR code with your authenticator app, then enter the 6-digit code.</p>
                {error && <div className="error">{error}</div>}

                <div style={{ display: 'flex', alignItems: 'center', gap: 16, margin: '12px 0' }}>
                    <div style={{ width: 160, height: 160, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--panel-bg,#f7f7f7)', borderRadius: 8 }}>
                        {loadingQr ? (
                            <span>Loading QR…</span>
                        ) : provisioningUri ? (
                            <QRCodeSVG value={provisioningUri} size={148} />
                        ) : (
                            <span>QR not available</span>
                        )}
                    </div>
                    <div style={{ fontSize: 13, color: 'var(--text-muted,#666)' }}>
                        1) Open your authenticator app (Google Authenticator, Authy, etc.)<br />
                        2) Add a new account and scan the QR code<br />
                        3) Enter the 6-digit code below
                    </div>
                </div>

                <div className="form-section">
                    <label htmlFor="otp">One-Time Password</label>
                    <input
                        id="otp"
                        type="text"
                        inputMode="numeric"
                        pattern="[0-9]{6}"
                        maxLength={6}
                        placeholder="123456"
                        value={otp}
                        onChange={(e) => setOtp(e.target.value.replace(/\D/g, ''))}
                        required
                        autoComplete="one-time-code"
                        disabled={loading}
                    />
                </div>

                <button type="submit" disabled={loading || otp.length !== 6}>
                    {loading ? 'Verifying…' : 'Verify & Complete Setup'}
                </button>

                <div style={{ marginTop: 12, textAlign: 'center' }}>
                    <button
                        type="button"
                        className="link-button"
                        onClick={() => navigate('/login')}
                        disabled={loading}
                    >
                        Use a different account
                    </button>
                </div>
            </form>
        </div>
    );
};

export default Setup2FA;